// Read-only Twitch chat over Twitch's public IRC WebSocket with an anonymous
// "justinfan" login: no account, no token, nothing leaves the browser but
// the channel name. Messages go to the listeners; votes are counted in
// votes.ts.

export interface ChatMessage {
  userId: string; // stable per viewer (tags user-id, else the login)
  name: string;
  text: string;
}

export type ChatStatus = 'off' | 'connecting' | 'connected' | 'error';

type Listener = (m: ChatMessage) => void;
type StatusListener = (s: ChatStatus) => void;

const URL = 'wss://irc-ws.chat.twitch.tv:443';

export const normalizeChannel = (c: string) =>
  c.trim().replace(/^https?:\/\/(www\.)?twitch\.tv\//i, '').replace(/^#/, '').split(/[/?#]/)[0].toLowerCase();

export const validChannel = (c: string) => /^[a-z0-9_]{3,25}$/.test(c);

/// Parses one IRC line with tags into a chat message (PRIVMSG only).
export function parseLine(line: string): ChatMessage | null {
  let rest = line;
  let tags: Record<string, string> = {};
  if (rest.startsWith('@')) {
    const sp = rest.indexOf(' ');
    tags = Object.fromEntries(
      rest
        .slice(1, sp)
        .split(';')
        .map((kv) => {
          const i = kv.indexOf('=');
          return [kv.slice(0, i), kv.slice(i + 1)];
        }),
    );
    rest = rest.slice(sp + 1);
  }
  const m = /^:([^!\s]+)![^\s]+ PRIVMSG #[^\s]+ :(.*)$/.exec(rest);
  if (!m) return null;
  const login = m[1];
  return {
    userId: tags['user-id'] || login,
    name: (tags['display-name'] || login).replace(/\\s/g, ' '),
    text: m[2],
  };
}

export class TwitchChat {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private joinTimer: ReturnType<typeof setTimeout> | null = null;
  status: ChatStatus = 'off';
  channel = '';

  onMessage(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onStatus(l: StatusListener): () => void {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  }

  private setStatus(s: ChatStatus) {
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  connect(channel: string): void {
    this.disconnect();
    this.channel = normalizeChannel(channel);
    if (!validChannel(this.channel)) {
      this.setStatus('error');
      return;
    }
    this.open();
  }

  private open() {
    this.setStatus('connecting');
    const ws = new WebSocket(URL);
    this.ws = ws;
    ws.onopen = () => {
      ws.send('CAP REQ :twitch.tv/tags twitch.tv/commands');
      ws.send('PASS SCHMOOPIIE');
      ws.send(`NICK justinfan${10000 + Math.floor(Math.random() * 80000)}`);
      ws.send(`JOIN #${this.channel}`);
      // No JOIN confirmation within 8 s: the channel probably doesn't exist.
      this.joinTimer = setTimeout(() => {
        if (this.status === 'connecting') this.setStatus('error');
      }, 8000);
    };
    ws.onmessage = (ev) => {
      for (const line of String(ev.data).split('\r\n')) {
        if (!line) continue;
        if (line.startsWith('PING')) {
          ws.send(line.replace('PING', 'PONG'));
          continue;
        }
        if (/ RECONNECT/.test(line) && !/ PRIVMSG /.test(line)) {
          ws.close();
          continue;
        }
        // ROOMSTATE only comes for channels that exist.
        if (/ ROOMSTATE #/.test(line)) {
          if (this.status !== 'connected') {
            if (this.joinTimer) clearTimeout(this.joinTimer);
            this.retry = 0;
            this.setStatus('connected');
          }
          continue;
        }
        const msg = parseLine(line);
        if (msg) for (const l of this.listeners) l(msg);
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return; // replaced or disconnected on purpose
      this.ws = null;
      // Reconnect with backoff (Twitch drops idle sockets now and then).
      const delay = Math.min(30000, 1000 * 2 ** this.retry++);
      this.setStatus(this.retry > 4 ? 'error' : 'connecting');
      this.timer = setTimeout(() => this.open(), delay);
    };
    ws.onerror = () => ws.close();
  }

  /// Hands [m] to the listeners as if it came from chat.
  emit(m: ChatMessage): void {
    for (const l of this.listeners) l(m);
  }

  disconnect(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.joinTimer) clearTimeout(this.joinTimer);
    this.timer = null;
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close();
    } catch {
      // ignore
    }
    this.retry = 0;
    this.setStatus('off');
  }
}

export const twitch = new TwitchChat();

// QA on localhost: window.__sgbChat('viewer', '2') fakes a chat message.
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  (window as unknown as { __sgbChat: (name: string, text: string) => void }).__sgbChat = (name, text) =>
    twitch.emit({ userId: name, name, text });
}

/// A chat message as a vote for option 1..[options], or null. Takes "2",
/// "!2", "!vote 2", "#2" and a leading number with an emote after it.
export function voteOf(text: string, options: number): number | null {
  const m = /^\s*(?:!vote\s*|!|#)?\s*(\d{1,2})(?!\d)/i.exec(text);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 && n <= options ? n : null;
}
